import { Suspense, useEffect, useMemo, useState } from "react";
import { Box, Paper } from "@mui/material";
import MButton from "../../../components/inputs/yup/materialui/mButton";
import SummarizeTwoToneIcon from "@mui/icons-material/SummarizeTwoTone";
import TableHeader from "../../../components/ui/materialui/tableHeader";
import Tabla from "../../../components/ui/materialui/serverSideTable";
import { exportExcel } from "../../../utils/exportExcel";
import { useDataFetch } from "../../../hooks/useDataFetch2";
import { useColumnsDividendosImpagos } from "../utils/columnsDividendosImpagos";
import ConfirmationModal from "../../../components/ui/materialui/confirmationModal";
import { updateCobranzaJudicial, validarCobranzaJudicial } from "../../maestros/cobranzaJudicial/service/serviceCobranzaJudicial";
import { StatusCode, TipoFuncionalidad } from "../../../utils/enums";
import { useToast } from "../../../hooks/useToast";
import FormConfirmationModal from "../../../components/ui/materialui/FormConfirmationModal";
import { GridDeleteIcon } from "@mui/x-data-grid";
import AddCircleOutlineTwoToneIcon from '@mui/icons-material/AddCircleOutlineTwoTone';
import { ValidarCobranzaJudicialDTO } from "../../maestros/cobranzaJudicial/models/cobranzaJudicial";
import { PermissionGuard } from "../../../components/auth/ProtectedRoute";
import { getCurrentUsername } from '../../../services/userRolesService';



interface IProps {
    idMutuo: number;
    isDesactivado?: boolean;
}

function DividendosImpagos({ idMutuo, isDesactivado }: IProps) {
    const urlTabla = 'siscred-api-cartera-total/v1/dividendos/datos-impago';
    const controlTabla = useDataFetch(urlTabla, new URLSearchParams({ idMutuo: idMutuo.toString() }), true);

    const { COLUMNS } = useColumnsDividendosImpagos({ controlTabla });
    const columns = useMemo(() => COLUMNS, [controlTabla]);
    const { addToast } = useToast();

    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [showModalAgregar, setShowModalAgregar] = useState(false);
    const [cobranzaJudicialIndicator, setCobranzaJudicialIndicator] = useState<boolean>(false);


    const abrirModal = async (tipo: "ELIMINAR" | "AGREGAR") => {
        const resp = await validarCobranzaJudicial(idMutuo);
        if (resp.status === StatusCode.Ok) {
            const tieneCobranza = resp.data;

            if (tipo === "ELIMINAR" && tieneCobranza === "N") {
                addToast({
                    mostrar: true,
                    titulo: "Advertencia",
                    tipo: "Warning",
                    mensaje: "Este mutuo no posee cobranza judicial."
                });
                return;
            }

            if (tipo === "AGREGAR" && tieneCobranza === "Y") {
                addToast({
                    mostrar: true,
                    titulo: "Advertencia",
                    tipo: "Warning",
                    mensaje: "Este mutuo ya posee cobranza judicial."
                });
                return;
            }
        }

        if (tipo === "ELIMINAR") {
            setShowModalEliminar(true);
        } else {
            setShowModalAgregar(true);
        }
    };

    const onConfirmarEliminar = async () => {
        const payload = [
            {
                id: idMutuo,
                esAdministrado: "Y",
                estado: "N",
                abogado: "",
                login: getCurrentUsername(),
            },
        ];

        const response = await updateCobranzaJudicial(payload);
        if (response.status === StatusCode.NoContent) {
            addToast({
                mostrar: true,
                titulo: "Éxito",
                tipo: "Success",
                mensaje: "Cobranza judicial eliminada exitosamente."
            });
            await evaluarCobranzaJudicial();
            // Notificar globalmente que el estado del mutuo cambió
            window.dispatchEvent(new CustomEvent('mutuo:estadoActualizado', { detail: { idMutuo, origen: 'eliminar' } }));
        }
        setShowModalEliminar(false);
    };

    const onConfirmarAgregar = async (data: { abogado: string }) => {
        const payload = [
            {
                id: idMutuo,
                esAdministrado: "Y",
                estado: "Y",
                abogado: data.abogado,
                login: getCurrentUsername(),
            },
        ];

        const response = await updateCobranzaJudicial(payload);
        if (response.status === StatusCode.NoContent) {
            addToast({
                mostrar: true,
                titulo: "Éxito",
                tipo: "Success",
                mensaje: "Cobranza judicial marcada exitosamente."
            });
            await evaluarCobranzaJudicial();
            // Notificar globalmente que el estado del mutuo cambió
            window.dispatchEvent(new CustomEvent('mutuo:estadoActualizado', { detail: { idMutuo, origen: 'agregar' } }));
        }
        setShowModalAgregar(false);
    };

    const evaluarCobranzaJudicial = async () => {
        const resp = await validarCobranzaJudicial(idMutuo);
        if (resp.status === StatusCode.Ok) {
            const respuesta = resp.data as ValidarCobranzaJudicialDTO;
            setCobranzaJudicialIndicator(respuesta.cobranzaJudicialIndicator);
        }
    };

    useEffect(() => {
        evaluarCobranzaJudicial();
    }, []);


    return (
        <>
            <Suspense>
                <Box sx={{ mt: 2 }}>
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <Tabla
                            columns={columns}
                            control={controlTabla}
                            url={urlTabla}
                            titulo={
                                <TableHeader
                                    title=""
                                    control={controlTabla}
                                    button={
                                        <PermissionGuard permissions={[TipoFuncionalidad.Cartera_adm_cobranza]}>
                                            <>
                                                <MButton
                                                    type="button"
                                                    variant="contained"
                                                    color="error"
                                                    startIcon={<GridDeleteIcon />}
                                                    onClick={() => abrirModal("ELIMINAR")}
                                                    disabled={!cobranzaJudicialIndicator || isDesactivado}
                                                >
                                                    Cobranza Judicial
                                                </MButton>
                                                <MButton
                                                    type="button"
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<AddCircleOutlineTwoToneIcon />}
                                                    onClick={() => abrirModal("AGREGAR")}
                                                    sx={{ ml: 1 }}
                                                    disabled={cobranzaJudicialIndicator || isDesactivado}
                                                >
                                                    Cobranza Judicial
                                                </MButton>
                                            </>
                                        </PermissionGuard>
                                    }
                                    buttonExport={
                                        <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                            <MButton
                                                type="button"
                                                variant="outlined"
                                                onClick={() =>
                                                    exportExcel(controlTabla.data, columns, "DIVIDENDOSIMPAGOS")
                                                }
                                                startIcon={<SummarizeTwoToneIcon />}
                                            >
                                                Exportar
                                            </MButton>
                                        </PermissionGuard>
                                    }
                                />
                            }
                        />
                    </Paper>
                </Box>
            </Suspense>

            <ConfirmationModal
                show={showModalEliminar}
                onHide={() => setShowModalEliminar(false)}
                onConfirm={onConfirmarEliminar}
                title="Eliminar cobranza judicial"
                message="¿Desea eliminar la cobranza judicial para este mutuo?"
            />

            <FormConfirmationModal
                open={showModalAgregar}
                title="Marcar cobranza judicial"
                subtitle="¿Desea marcar cobranza judicial para este mutuo?."
                confirmText="Marcar"
                cancelText="Cancelar"
                inputName="abogado"
                inputLabel="Abogado estudio"
                onClose={() => setShowModalAgregar(false)}
                onConfirm={onConfirmarAgregar}
            />
        </>
    );
}

export default DividendosImpagos;
